import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import DateRange from '../DateRange';
import { useCloseOnEscape } from './closeOnEscape';
import { useMenuHeight } from './menuPlacement';
import { pillShape } from './MultiSelectPill';
import './FilterPill.css';

const valueWidth = { maxWidth: '18rem' };

export const dateRangeFormat = 'MM/DD/YYYY';

export interface DateRangeValue {
  start: string;
  end: string;
}

export interface DateRangePillProps {
  id: string;
  label: string;
  icon?: string;
  range?: DateRangeValue;
  onChange: (range?: DateRangeValue) => void;
}

dayjs.extend(customParseFormat);

const isValid = (date: string) => dayjs(date, dateRangeFormat, true).isValid();

export default function DateRangePill(props: DateRangePillProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>();
  const pillRef = useRef<HTMLDivElement>(null);
  const { maxHeight, measure } = useMenuHeight(pillRef, open);

  const defaultRange = {
    start: dayjs().subtract(1, 'month').format(dateRangeFormat),
    end: dayjs().format(dateRangeFormat),
  };

  const shown = draft ?? props.range ?? defaultRange;
  const active = !!props.range;
  const bothDatesValid = isValid(shown.start) && isValid(shown.end);

  const show = () => {
    measure();
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setDraft(undefined);
  };

  useCloseOnEscape(pillRef, open, close);

  return (
    <div className="d-inline-flex align-items-stretch mw-100" ref={pillRef}>
      <Dropdown show={open} onToggle={next => next ? show() : close()} autoClose="outside">
        <Dropdown.Toggle
          as="button"
          type="button"
          id={`${props.id}-toggle`}
          className={`filter-pill d-inline-flex align-items-center text-nowrap border bg-white${active ? ' active filter-pill-clearable' : ''}`}
          style={pillShape}>
          {props.icon && <i className={`bi-${props.icon} me-2`} aria-hidden="true" />}
          <span className="fw-semibold text-truncate" style={valueWidth}>
            {active ? `${props.range!.start} - ${props.range!.end}` : props.label}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="filter-pill-menu pt-1 shadow-lg overflow-hidden"
          style={{ minWidth: 'min(24rem, calc(100vw - 2rem))', maxHeight }}
          popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }] }}>
          <div className="overflow-auto flex-grow-1 px-3 pt-2 pb-1" style={{ minHeight: 0 }}>
            <DateRange
              className="row g-2 filter-pill-dates"
              startDate={shown.start}
              endDate={shown.end}
              dateFormat={dateRangeFormat}
              rangeUpdated={(start, end) => setDraft({ start, end })} />
          </div>
          <div className="dropdown-divider"></div>
          <div className="d-flex px-3 pb-2 pt-1 flex-shrink-0">
            <button
              type="button"
              aria-label={`Apply ${props.label}`}
              className={`btn btn-sm text-nowrap border w-100 ${bothDatesValid ? 'btn-outline-primary border-primary' : 'text-muted'}`}
              disabled={!bothDatesValid}
              onClick={() => {
                props.onChange(shown);
                close();
              }}>
              Apply
            </button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
      {active &&
        <button
          type="button"
          className="filter-pill-clear"
          aria-label={`Clear ${props.label}`}
          onClick={() => props.onChange(undefined)}>
          <i className="bi-x-lg" aria-hidden="true" />
        </button>}
    </div>
  );
}
